import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { getToken, getSubject, onAuthChange, clearAuth, fetchMe } from '../api/auth'

const AuthContext = createContext(null)

/**
 * Provides auth state (token + subject role/scope) to the app.
 * Token is held in memory (see api/auth.js); a page refresh requires re-login.
 */
export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(getToken())
  const [subject, setSubjectState] = useState(getSubject())
  const [bootstrapping, setBootstrapping] = useState(Boolean(getToken()))

  useEffect(() => {
    const unsub = onAuthChange(({ token, subject }) => {
      setTokenState(token)
      setSubjectState(subject)
    })
    return unsub
  }, [])

  // Re-verify token against the server on initial bootstrap
  useEffect(() => {
    let cancelled = false
    async function bootstrap() {
      if (getToken()) {
        try {
          const me = await fetchMe()
          if (!cancelled) setSubjectState(me)
        } catch {
          if (!cancelled) clearAuth()
        }
      }
      if (!cancelled) setBootstrapping(false)
    }
    bootstrap()
    return () => {
      cancelled = true
    }
  }, [token])

  const logout = useCallback(() => clearAuth(), [])

  const value = {
    token,
    subject,
    role: subject?.role,
    scope: subject?.scope || [],
    isAuthenticated: Boolean(token),
    bootstrapping,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
